import React, { useEffect, useState } from "react";
import {
  Box, Grid, Typography, Button,
  Paper,
  Table,
  TableCell,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import GroupIcon from "@mui/icons-material/Group";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import BusinessIcon from "@mui/icons-material/Business";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { OverAllLoader } from "../../components/OverAllLoader";
import api from "../../api/client";
import { useLocation, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import EditIcon from '@mui/icons-material/Edit';
import { useChangeableItems, getLabelByKey } from "../../store/useDynamicLabel";
import { BrainCircuitIcon } from "lucide-react";

const ViewStudent = () => {

  const changeableItems = useChangeableItems();
  const getLabel = (itemKey) => getLabelByKey(changeableItems, itemKey);
  const organizationId = localStorage.getItem("organization_id")

  const [overAllLoading, setOverAllLoading] = useState(false);
  const [studentData, setStudentData] = useState([]);
  const location = useLocation();
  const { StudentId } = location.state || "";
  const navigate = useNavigate();

  const handleNavigate = () => {
    navigate("/addStudent", {
      state: {
        StudentId: studentData?.user_id,
      },
    });
  };

  const fetchById = async () => {
    setOverAllLoading(true);
    try {
      const res = await api.post("ViewStudentByID", {
        user_id: StudentId,
      });
      setStudentData(res?.data?.data);
      setOverAllLoading(false);
    } catch (error) {
      console.error("Error fetching admin by ID:", error);
      setOverAllLoading(false);
    }
  };

  useEffect(() => {
    fetchById();
  }, []);

  return (
    <Box sx={{ minHeight: "100vh", p: 1 }}>
      {overAllLoading ? (
        <OverAllLoader message="Fetching student details..." />
      ) : (
        <Box>
          {/* Back Button */}
          <Grid
            item
            sx={{
              display: "flex",
              alignItems: "center",
              color: "#1c97e3",
              gap: "4px",
              cursor: "pointer",
              width: "fit-content",
              mb: 2,
            }}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                window.history.back();
              }
            }}
            onClick={() => window.history.back()}
          >
            <ArrowBackIcon sx={{ fontSize: "17px" }} />
            <Typography
              sx={{ fontWeight: 600, fontSize: "14px", color: "#1c97e3" }}
            >
              Back
            </Typography>
          </Grid>

          {/* View Card */}
          <Box>
            {/* Header */}
            <Grid
              sx={{
                display: "flex",
                justifyContent: "space-between",
                borderBottom: "1px solid #d1d1d1",
              }}
            >
              <Grid
                sx={{
                  mb: 2,
                  display: "flex",
                  gap: 1,
                  alignItems: "center",
                }}
              >
                <Grid
                  sx={{
                    backgroundColor: "#1c97e3",
                    p: 0.5,
                    borderRadius: "10px",
                  }}
                >
                  <GroupIcon sx={{ color: "white", fontSize: "35px" }} />
                </Grid>
                <Grid>
                  <Typography sx={{ fontSize: "30px", fontWeight: "bold" }}>
                    {getLabel('STUDENT')} Details
                  </Typography>
                </Grid>
              </Grid>
              <Grid>
                <Button
                  variant="contained"
                  className="gradient-button"
                  startIcon={<EditIcon />}
                  onClick={handleNavigate}
                >
                  Edit
                </Button>
              </Grid>
            </Grid>

            {/* Student Details */}
            <Box
              sx={{ p: 3, display: "flex", flexDirection: "column", rowGap: 3 }}
            >
              {/* Basic Info */}
              <Grid sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <GroupIcon sx={{ color: "#1c97e3", fontSize: "30px" }} />
                <Typography sx={{ fontSize: "20px", fontWeight: "bold" }}>
                  Basic Information
                </Typography>
              </Grid>
              <Grid
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  rowGap: 2,
                  width: "100%",
                }}
              >
                <Grid
                  sx={{
                    display: "flex",
                    flex: "row",
                  }}
                >
                  <Grid item xs={6} sx={{ width: "50%" }}>
                    <Typography fontWeight="600">First Name</Typography>
                    <Typography>{studentData.first_name || "-"}</Typography>
                  </Grid>
                  <Grid item xs={6} sx={{ width: "50%" }}>
                    <Typography fontWeight="600">Last Name</Typography>
                    <Typography>{studentData.last_name || "-"}</Typography>
                  </Grid>
                </Grid>
                <Grid
                  sx={{
                    display: "flex",
                    flex: "row",
                  }}
                >
                  <Grid item xs={6} sx={{ width: "50%" }}>
                    <Typography fontWeight="600">Gender</Typography>
                    <Typography>{studentData.gender || "-"}</Typography>
                  </Grid>
                  <Grid item xs={6} sx={{ width: "50%" }}>
                    <Typography fontWeight="600">Date of Birth</Typography>
                    <Typography>{studentData.dob || "-"}</Typography>
                  </Grid>
                </Grid>
                <Grid
                  sx={{
                    display: "flex",
                    flex: "row",
                  }}
                >
                  <Grid item xs={6} sx={{ width: "50%" }}>
                    <Typography fontWeight="600">Created By</Typography>
                    <Typography>{studentData.created_by || "-"}</Typography>
                  </Grid>
                  <Grid item xs={6} sx={{ width: "50%" }}>
                    <Typography fontWeight="600">Created Date</Typography>
                    <Typography>
                      {dayjs(studentData.created_at).format("DD-MM-YYYY") || "-"}
                    </Typography>
                  </Grid>
                </Grid>
                <Grid
                  sx={{
                    display: "flex",
                    flex: "row",
                  }}
                >
                  <Grid item xs={6} sx={{ width: "50%" }}>
                    <Typography fontWeight="600">Updated By</Typography>
                    <Typography>{studentData.updated_by || "-"}</Typography>
                  </Grid>
                  <Grid item xs={6} sx={{ width: "50%" }}>
                    <Typography fontWeight="600">Updated Date</Typography>
                    <Typography>
                      {dayjs(studentData.updated_at).format("DD-MM-YYYY") || "-"}
                    </Typography>
                  </Grid>
                </Grid>

                {organizationId === "2" && (
                  <Grid
                    sx={{
                      display: "flex",
                      flex: "row",
                    }}
                  >
                    <Grid item xs={6} sx={{ width: "50%" }}>
                      <Typography fontWeight="600">UG Qualification</Typography>
                      <Typography>{studentData.candidate_profile?.ug_qualification || "-"}</Typography>
                    </Grid>
                    <Grid item xs={6} sx={{ width: "50%" }}>
                      <Typography fontWeight="600">PG Qualification</Typography>
                      <Typography>
                        {studentData.candidate_profile?.pg_qualification || "-"}
                      </Typography>
                    </Grid>
                  </Grid>
                )}

                <Grid
                  sx={{
                    display: "flex",
                    flex: "row",
                  }}
                >
                  <Grid item xs={6} sx={{ width: "50%" }}>
                    <Typography fontWeight="600">Status</Typography>
                    <Typography>{studentData.status || "-"}</Typography>
                  </Grid>
                </Grid>
              </Grid>

              {/* Contact Info */}
              <Grid sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <MailOutlineIcon sx={{ color: "#1c97e3", fontSize: "30px" }} />
                <Typography sx={{ fontSize: "20px", fontWeight: "bold" }}>
                  Contact Information
                </Typography>
              </Grid>
              <Grid
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  rowGap: 2,
                }}
              >
                <Grid sx={{ display: "flex" }}>
                  <Grid item xs={6} sx={{ width: "50%" }}>
                    <Typography fontWeight="600">Contact Number</Typography>
                    <Typography>{studentData.phone || "-"}</Typography>
                  </Grid>
                  <Grid item xs={6} sx={{ width: "50%" }}>
                    <Typography fontWeight="600">Email</Typography>
                    <Typography>{studentData.email || "-"}</Typography>
                  </Grid>
                </Grid>

                {organizationId === '2' && (
                  <>
                    <Grid sx={{ display: "flex" }}>
                      <Grid item xs={6} sx={{ width: "50%" }}>
                        <Typography fontWeight="600">Address</Typography>
                        <Typography>{studentData.address || "-"}</Typography>
                      </Grid>

                      <Grid item xs={6} sx={{ width: "50%" }}>
                        <Typography fontWeight="600">City</Typography>
                        <Typography>{studentData.city || "-"}</Typography>
                      </Grid>
                    </Grid>

                    <Grid sx={{ display: "flex" }}>
                      <Grid item xs={6} sx={{ width: "50%" }}>
                        <Typography fontWeight="600">State</Typography>
                        <Typography>{studentData.state || "-"}</Typography>
                      </Grid>

                      <Grid item xs={6} sx={{ width: "50%" }}>
                        <Typography fontWeight="600">Pincode</Typography>
                        <Typography>{studentData.pincode || "-"}</Typography>
                      </Grid>
                    </Grid>
                  </>
                )}
              </Grid>

              <Grid
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  rowGap: 2,
                }}
              >
              </Grid>
              {/* Institute Info */}
              <Grid sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <BusinessIcon sx={{ color: "#1c97e3", fontSize: "30px" }} />
                <Typography sx={{ fontSize: "20px", fontWeight: "bold" }}>
                  {getLabel('INSTITUTE')} Information
                </Typography>
              </Grid>
              <Grid
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  rowGap: 2,
                }}
              >
                <Grid sx={{ display: "flex" }}>
                  <Grid item xs={6} sx={{ width: "50%" }}>
                    <Typography fontWeight="600"> {getLabel('INSTITUTE')}  Name</Typography>
                    <Typography>{studentData.institute_name || "-"}</Typography>
                  </Grid>
                  <Grid item xs={6} sx={{ width: "50%" }}>
                    <Typography fontWeight="600"> {getLabel('BATCHES')}  Name</Typography>
                    <Typography>
                      {studentData.batch_names?.length
                        ? studentData.batch_names.join(", ")
                        : studentData.batch_name || "-"}
                    </Typography>
                  </Grid>
                </Grid>

                {organizationId === "2" && (
                  <Grid sx={{ display: "flex" }}>
                    <Grid item xs={6} sx={{ width: "50%" }}>
                      <Typography fontWeight="600"> Total Experience</Typography>
                      <Typography>{studentData.candidate_profile?.total_experience_years || "-"}</Typography>
                    </Grid>
                    <Grid item xs={6} sx={{ width: "50%" }}>
                      <Typography fontWeight="600"> Designation</Typography>
                      <Typography>{studentData.candidate_profile?.designation || "-"}</Typography>
                    </Grid>
                  </Grid>
                )}

                {localStorage.getItem("role") === "INSTITUTE_ADMIN" && (
                  <Grid sx={{ display: "flex" }}>
                    <Grid item xs={6} sx={{ width: "50%" }}>
                      <Typography fontWeight="600">
                        Assigned {getLabel('TRAINER')}
                      </Typography>
                      <Typography>
                        {studentData.assigned_trainers &&
                          studentData.assigned_trainers.length > 0
                          ? studentData.assigned_trainers
                            .map((t) => t.trainer_name)
                            .join(", ")
                          : "Not Assigned Yet"}
                      </Typography>
                    </Grid>
                  </Grid>
                )}

                {/* Skills Info */}
                {organizationId === "2" && (
                  <>
                    < Grid sx={{ display: "flex", gap: 1 }}>
                      <BrainCircuitIcon
                        style={{
                          color: "#1c97e3",
                          height: "27px",
                          width: "27px",
                          fontWeight: "bold",
                        }}
                      />
                      <Typography sx={{ fontSize: "20px", fontWeight: "bold" }}>
                        {`${getLabel('SUBJECT')} Information`}
                      </Typography>
                    </Grid>

                    <Grid sx={{ display: "flex", gap: 3 }}>
                      <TableContainer
                        component={Paper}
                      >
                        <Table>
                          <TableHead>
                            <TableRow className="tableHeader">
                              <TableCell sx={{ py: 1, width: '250px' }} className="tableHeader">
                                <b>Skill</b>
                              </TableCell>

                              <TableCell
                                className="tableHeader"
                                sx={{ width: "250px", py: 1 }}
                              >
                                <b>Proficiency</b>
                              </TableCell>

                              <TableCell
                                className="tableHeader"
                                sx={{ width: "250px", py: 1 }}
                              >
                                <b>Relevant Experience</b>
                              </TableCell>
                            </TableRow>
                          </TableHead>

                          <TableBody className="tablebody">
                            {studentData?.candidate_profile?.skills.map((skill, index) => {
                              return (
                                <TableRow
                                  key={skill.skill_id}
                                  sx={{
                                    "& .MuiTableCell-root": {
                                      borderBottom: "1px solid #eeeeee",
                                      fontSize: "13px",
                                    },
                                    backgroundColor:
                                      index % 2 === 1
                                        ? "#FAFAFA"
                                        : "inherit",
                                  }}
                                >
                                  {/* Skill */}
                                  <TableCell>
                                    <Typography
                                      sx={{
                                        fontSize: "14px",
                                        fontWeight: 500,
                                      }}
                                    >
                                      {skill.name}
                                    </Typography>
                                  </TableCell>

                                  {/* Proficiency */}
                                  <TableCell>
                                    <Typography
                                      sx={{
                                        fontSize: "14px",
                                        fontWeight: 500,
                                      }}
                                    >
                                      {skill.proficiency}
                                    </Typography>
                                  </TableCell>

                                  {/* Relevant Experience */}
                                  <TableCell>
                                    <Typography
                                      sx={{
                                        fontSize: "14px",
                                        fontWeight: 500,
                                      }}
                                    >
                                      {skill.years_of_experience}
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Grid>
                  </>
                )}
              </Grid>
            </Box>
          </Box>
        </Box>
      )
      }
    </Box >
  );
};

export default ViewStudent;
